import Delegate from "ftdomdelegate";
import { add, remove, qs } from "@fluorescent/dom";
import { createFocusTrap } from "focus-trap";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";

import { section, cart } from "@/glow";
import { on } from "@/glow/events";
import { makeRequest } from "@/lib/xhr";
import QuantityButtons from "@/lib/quantity-buttons";
import CartNoteToggle from "@/lib/cart-note-toggle";
import updateInnerHTML from "@/lib/update-inner-html";
import FreeShippingBar from "@/lib/free-shipping-bar";
import { animateQuickCart, shouldAnimate } from "@/lib/animation";

const selectors = {
  cartWrapper: ".quick-cart__wrapper",
  innerContainer: ".quick-cart__container",
  overlay: ".quick-cart__overlay",
  closeButton: ".quick-cart__close-icon",
  footer: ".quick-cart__footer",
  items: ".quick-cart__items",
  cartError: ".quick-cart__item-error",
  form: ".quick-cart__form",
  cartCount: ".quick-cart__heading sup",
  subtotal: ".quick-cart__footer-subtotal span",
  quantityInput: ".quick-cart .quantity-input__input",
  quantityItem: "[data-input-item]",
  discounts: ".quick-cart__item-discounts",
  freeShippingBar: "[data-free-shipping-bar]",
};

const classes = {
  active: "active",
  hidden: "hidden",
  updatingQuantity: "has-quantity-update",
  removed: "is-removed",
};

section("quick-cart", {
  onLoad() {
    this.cartWrapper = qs(selectors.cartWrapper, this.container);
    this.cartTrap = createFocusTrap(this.container, {
      allowOutsideClick: true,
    });

    // Events are all on events trigger by other components / functions
    this.events = [
      on("quick-cart:open", () => this.openQuickCart()),
      on("quick-cart:updated", () => this.refreshQuickCart()),
      on("quick-cart:error", (_, { id, errorMessage }) => {
        this.handleErrorMessage(id, errorMessage);
      }),
      on(
        ["quantity-update:subtract", "quantity-update:add"],
        (_, { itemId }) => {
          this.handleQuantityUpdate(itemId);
        }
      ),
      on("quantity-update:remove", (_, { itemId }) => {
        this.handleItemRemoval(itemId);
      }),
    ];

    this.quantityButtons = QuantityButtons(this.container);
    this.cartNoteToggle = CartNoteToggle(this.container);

    if (shouldAnimate(this.container)) {
      this.animateQuickCart = animateQuickCart(this.container);
    }

    // Delegate handles all click events due to rendering different content
    // within quick cart
    this.delegate = new Delegate(this.container);

    this.delegate.on("click", selectors.overlay, () => this.close());
    this.delegate.on("click", selectors.closeButton, () => this.close());
    this.delegate.on("change", selectors.quantityInput, e =>
      this.handleQuantityInputChange(e)
    );

    const freeShippingBar = qs(selectors.freeShippingBar, this.container);

    if (freeShippingBar) {
      FreeShippingBar(freeShippingBar);
    }
  },

  openQuickCart() {
    add(this.cartWrapper, classes.active);
    this.cartTrap.activate();
    this.adjustItemPadding();
    this.animateQuickCart?.open();

    disableBodyScroll(this.container, {
      allowTouchMove: el => {
        while (el && el !== document.body) {
          if (el.getAttribute("data-scroll-lock-ignore") !== null) {
            return true;
          }
          el = el.parentNode;
        }
      },
      reserveScrollBarGap: true,
    });
  },

  refreshQuickCart() {
    const url = `${theme.routes.cart.base}?section_id=quick-cart`;

    makeRequest("GET", url).then(response => {
      let container = document.createElement("div");
      container.innerHTML = response;
      const responseInnerContainer = qs(selectors.innerContainer, container);
      const cartHasItems = Boolean(qs(selectors.items, this.container));
      const responseHasItems = Boolean(qs(selectors.items, container));
      const freeShippingBar = qs(selectors.freeShippingBar, container);

      if (freeShippingBar) {
        FreeShippingBar(freeShippingBar);
      }

      // Cart has items and needs to update them
      if (responseHasItems && cartHasItems) {
        // Render cart items
        updateInnerHTML(
          `${selectors.cartWrapper} ${selectors.items}`,
          container
        );
        this.adjustItemPadding();

        // Render cart count
        updateInnerHTML(
          `${selectors.cartWrapper} ${selectors.cartCount}`,
          container
        );

        // Render subtotal
        updateInnerHTML(
          `${selectors.cartWrapper} ${selectors.subtotal}`,
          container
        );

        // Render promotions
        updateInnerHTML(
          `${selectors.cartWrapper} ${selectors.discounts}`,
          container
        );

        // Handle form scroll state
        const form = qs(selectors.form, this.container);
        const previousScrollPosition = form.scrollTop || 0;
        form.scrollTop = previousScrollPosition;
        this.animateQuickCart?.setup();
      } else {
        // Cart needs to render empty from having items, or needs to render
        // items from empty state
        const innerContainer = qs(selectors.innerContainer, this.container);
        innerContainer.innerHTML = responseInnerContainer.innerHTML;
      }
    });
  },

  handleErrorMessage(itemId) {
    const item = qs(`[data-id="${itemId}"]`, this.container);
    remove(qs(selectors.cartError, item), classes.hidden);
    remove(item, classes.updatingQuantity);
  },

  handleQuantityUpdate(itemId) {
    const item = qs(`[data-id="${itemId}"]`, this.container);
    add(item, classes.updatingQuantity);
  },

  handleItemRemoval(itemId) {
    const item = qs(`[data-id="${itemId}"]`, this.container);
    add(item, classes.removed);
    add(item, classes.updatingQuantity);
  },

  handleQuantityInputChange({ target }) {
    const item = target.closest(selectors.quantityItem);
    const itemId = item.dataset.id;

    cart.updateItem(itemId, target.value);
    this.handleQuantityUpdate(itemId);
  },

  adjustItemPadding() {
    const items = qs(selectors.items, this.container);

    if (!items) return;

    // Ensure cart items accounts for the height of cart footer
    const footer = qs(selectors.footer, this.container);
    items.style.paddingBottom = `${footer.clientHeight}px`;
  },

  close() {
    remove(this.cartWrapper, classes.active);
    setTimeout(() => {
      this.animateQuickCart?.close();
      this.cartTrap.deactivate();
      enableBodyScroll(this.container);
    }, 500);
  },

  onSelect() {
    this.openQuickCart();
  },

  onDeselect() {
    this.close();
  },

  onUnload() {
    this.delegate.off();
    this.events.forEach(unsubscribe => unsubscribe());
    this.quantityButtons.unload();
    this.cartNoteToggle.unload();
  },
});
